import React, { useState, useEffect, useCallback, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import {Backdrop, CircularProgress, TableCell, tableCellClasses, useMediaQuery} from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Menu from "@mui/material/Menu";
import { styled } from '@mui/system';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import MidiOutSelector from "./MidiOutSelector";
import ChannelSelector from "./ChannelSelector";
import OutLevelSelector from "./OutLevelSelector";
import OutLevelSelectorClickAway from "./OutLevelSelectorClickAway";
import DeviceIdSelector from "./DeviceIdSelector";
import {globalChangeMessage, MidiAccessStatus, outLevelAddress} from "./utils";

const StyledBackdrop = styled(Backdrop)`
  z-index: ${(props) => (props.theme && props.theme.zIndex ? props.theme.zIndex.drawer + 1 : 1200)};
  color: #fff;
`;

const Overlay = styled('div')({
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(127, 255, 127, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    border: '30px solid',
    borderColor: 'rgba(127, 255, 127, 1)',
    padding: '10px',
    borderRadius: '100px',
    fontSize: '30px',
});

const StyledTableContainer = styled(TableContainer)({
    position: 'fixed',
    top: 'calc(250px + 48px)',
    height: 'calc(100vh - 390px - 48px)',
    overflow: 'auto',
});

const HeaderStyledTTable = styled(Table)({
    position: 'fixed',
    top: '48px',
    width: '100%',
});

const FooterStyledTTable = styled(Table)({
    position: 'fixed',
    bottom: 0,
    width: '100%',
});

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function DroppablBody({ onFileContents, children }) {
    const onDrop = useCallback(acceptedFiles => {
        acceptedFiles.forEach((file) => {
            const reader = new FileReader();

            reader.onabort = () => console.log('file reading was aborted');
            reader.onerror = () => console.log('file reading has failed');
            reader.onload = () => {
                onFileContents(reader.result);
            };
            reader.readAsText(file);
        });
    }, [onFileContents]);

    const { getRootProps, getInputProps, isDragActive  } = useDropzone({　noClick: true, onDrop });

    return (
        <div {...getRootProps()}　>
            <input {...getInputProps()} />
            <Table>
                {children}
            </Table>
            {isDragActive && <Overlay>Exportデータを読み込みます</Overlay>}
        </div>
    );
}

export default　function App({ midiAccessStatus, midiOutputs }) {
    const [data, setData] = useState(() => {
        const savedData = localStorage.getItem('data');
        return savedData ? JSON.parse(savedData) : [];
    });
    const [tsv, setTsv] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const selectedRowRef = useRef(null);
    const [title, setTitle] = useState('');
    const [bank, setBank] = useState(() => {
        const savedData = localStorage.getItem('bank');
        return savedData ? JSON.parse(savedData) : 'UA';
    });
    const [patch, setPatch] = useState(() => {
        const savedData = localStorage.getItem('patch');
        return savedData ? JSON.parse(savedData) : 1;
    });
    const [outLevel, setOutLevel] = useState(() => {
        const savedOutLevel = localStorage.getItem('outLevel');
        return savedOutLevel ? JSON.parse(savedOutLevel) : 50;
    })
    const [bank2, setBank2] = useState(() => {
        const savedData = localStorage.getItem('bank2');
        return savedData ? JSON.parse(savedData) : 'UA';
    });
    const [patch2, setPatch2] = useState(() => {
        const savedData = localStorage.getItem('patch2');
        return savedData ? JSON.parse(savedData) : 1;
    });
    const [outLevel2, setOutLevel2] = useState(() => {
        const savedOutLevel2 = localStorage.getItem('outLevel2');
        return savedOutLevel2 ? JSON.parse(savedOutLevel2) : 50;
    });
    const [tabIndex, setTabIndex] = useState(0);
    const [midiOutId, setMidiOutId] = useState(() => {
        const savedData = localStorage.getItem('midiOutId');
        return savedData ? JSON.parse(savedData) : null;
    });
    const [midiOutId2, setMidiOutId2] = useState(() => {
        const savedData = localStorage.getItem('midiOutId2');
        return savedData ? JSON.parse(savedData) : null;
    });
    const [midiChannel, setMidiChannel] = useState(() => {
        const savedData = localStorage.getItem('midiChannel');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number > 0 && number < 17 ? number : 1;
    });
    const [midiChannel2, setMidiChannel2] = useState(() => {
        const savedData = localStorage.getItem('midiChannel2');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number > 0 && number < 17 ? number : 2;
    });
    const [deviceId, setDeviceId] = useState(() => {
        const savedData = localStorage.getItem('deviceId');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number >= 0 && number < 32 ? number : 0;
    });
    const [deviceId2, setDeviceId2] = useState(() => {
        const savedData = localStorage.getItem('deviceId2');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number >= 0 && number < 32 ? number : 1;
    });
    const [contextMenu, setContextMenu] = React.useState(null);
    const [midiOut, setMidiOut] = useState(null);
    const [midiOut2, setMidiOut2] = useState(null);

    const isSmallScreen = useMediaQuery('(max-width:700px)');

    // const [midiOutIdSrv33, setMidiOutIdSrv33] = useState(() => {
    //     const savedData = localStorage.getItem('midiOutIdSrv33');
    //     return savedData ? JSON.parse(savedData) : null;
    // });
    // const [midiChannelSrv33, setMidiChannelSrv33] = useState(() => {
    //     const savedData = localStorage.getItem('midiChannelSrv33');
    //     const number = parseInt(savedData ? JSON.parse(savedData) : 1);
    //     return number > 0 && number < 17 ? number : 1;
    // });
    // // const [deviceIdSrv33, setDeviceIdSrv33] = useState(() => {
    // //     const savedData = localStorage.getItem('deviceIdSrv33');
    // //     const number = parseInt(savedData ? JSON.parse(savedData) : 1);
    // //     return number >= 0 && number < 32 ? number : 0;
    // // });
    // const [midiOutSrv33, setMidiOutSrv33] = useState(null);

    const SendMidiSettings = useCallback(() => {
        localStorage.setItem('midiChannel', JSON.stringify(midiChannel));
        localStorage.setItem('midiChannel2', JSON.stringify(midiChannel2));
        localStorage.setItem('bank', JSON.stringify(bank));
        localStorage.setItem('patch', JSON.stringify(patch));
        localStorage.setItem('bank2', JSON.stringify(bank2));
        localStorage.setItem('patch2', JSON.stringify(patch2));

        if (!(
            Number.isFinite(parseFloat(patch)) && Number.isFinite(parseFloat(patch2))
            && parseFloat(patch) > 0 && parseFloat(patch2) > 0
            && parseFloat(patch) <= 101 && parseFloat(patch2) <= 101
        )) {
            return;
        }

        function SendMidiSettingsWithOutput(outputArg, channel, bankArg, patchArg ) {
            let bankNum;
            switch (bankArg) {
                case 'PA':
                    bankNum = 2;
                    break;
                case 'PB':
                    bankNum = 3;
                    break;
                case 'UA':
                    bankNum = 4;
                    break;
                case 'UB':
                    bankNum = 5;
                    break;
                default:
                    break;
            }
            const bankSelectMSB = [0xB0 + channel, 0x00, bankNum]; // Bank Select MSB
            const programChangeMessage = [0xC0 + channel, patchArg - 1];　// bank
            outputArg.send(bankSelectMSB);
            outputArg.send(programChangeMessage);
        }

        if (midiOut) {
            SendMidiSettingsWithOutput(midiOut, midiChannel - 1, bank, patch);
        }
        if (midiOut2) {
            SendMidiSettingsWithOutput(midiOut2, midiChannel2 - 1, bank2, patch2);
        }
    }, [midiOut, midiOut2, midiChannel, midiChannel2, bank, patch, bank2, patch2]);

    useEffect( () => {
        SendMidiSettings();
    }, [SendMidiSettings])

    useEffect(() => {
        localStorage.setItem('data', JSON.stringify(data));
        setTsv(toTsv(data));
    }, [data]);

    useEffect(() => {
        localStorage.setItem('outLevel', JSON.stringify(outLevel));
        localStorage.setItem('deviceId', JSON.stringify(deviceId));
        globalChangeMessage(midiOut, deviceId, outLevel, outLevelAddress);
    }, [deviceId, midiOut, outLevel]);

    useEffect(() => {
        localStorage.setItem('outLevel2', JSON.stringify(outLevel2));
        localStorage.setItem('deviceId2', JSON.stringify(deviceId2));
        globalChangeMessage(midiOut2, deviceId2, outLevel2, outLevelAddress);
    }, [deviceId2, midiOut2, outLevel2]);

    useEffect(() => {
        localStorage.setItem('midiOutId', JSON.stringify(midiOutId));
    }, [midiOutId]);

    useEffect(() => {
        localStorage.setItem('midiOutId2', JSON.stringify(midiOutId2));
    }, [midiOutId2]);

    // useEffect(() => {
    //     localStorage.setItem('midiOutIdSrv33', JSON.stringify(midiOutIdSrv33));
    // }, [midiOutIdSrv33]);
    //
    // useEffect(() => {
    //     localStorage.setItem('midiChannelSrv33', JSON.stringify(midiChannelSrv33));
    // }, [midiChannelSrv33]);
    //
    // // useEffect(() => {
    // //     localStorage.setItem('deviceIdSrv33', JSON.stringify(deviceIdSrv33));
    // // }, [deviceIdSrv33]);

    const toTsv = (data) => {
        return data.map(row => [row.title, row.bank, row.patch, row.outLevel, row.bank2, row.patch2, row.outLevel2].join('\t')).join('\n');
    }

    const fromTsv = (tsv) => {
        return tsv.split('\n')
            .filter(line => !/^\s*$/.test(line))
            .map((line, i) => {
                const [title, bank, patch, outLevel, bank2, patch2, outLevel2] = line.split('\t');
                return modifyRow({ id: i+1, title, bank, patch, outLevel, bank2, patch2, outLevel2 });
            });
    }

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleTsvChange = (event) => {
        setTsv(event.target.value);
        setData(fromTsv(event.target.value));
    };

    const modifyRow = (row) => {
        const regBank = /[UP][AB]/;
        const regNumber = /\d{1,3}/;
        const bank = regBank.test(row.bank) ? row.bank.match(regBank)[0] : 'UA';
        const patch = Number.isFinite(row.patch) ? row.patch : regNumber.test(row.patch) ? row.patch.match(regNumber)[0] : 1;
        const outLevel = Number.isFinite(row.outLevel) ? row.outLevel : regNumber.test(row.outLevel) ? row.outLevel.match(regNumber)[0] : 50;
        const bank2 = regBank.test(row.bank2) ? row.bank2.match(regBank)[0] : 'UA';
        const patch2 = Number.isFinite(row.patch2) ? row.patch2 : regNumber.test(row.patch2) ? row.patch2.match(regNumber)[0] : 1;
        const outLevel2 = Number.isFinite(row.outLevel2) ? row.outLevel2 :regNumber.test(row.outLevel2) ? row.outLevel2.match(regNumber)[0] : 50;
        return { ...row, bank, patch, outLevel, bank2, patch2, outLevel2}
    }

    const scrollToSelectedRow = () => {
        setTimeout(() => {
            if (selectedRowRef.current) {
                selectedRowRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }, 100);
    }

    const handleAdd = () => {
        const id = data.length ? data[data.length - 1].id + 1 : 1;
        const newRow = { id, title, bank, patch, outLevel, bank2, patch2, outLevel2 };
        setData([...data, newRow]);
        handleRowClick(newRow);
        scrollToSelectedRow();
        // setTitle('');
        // setBank('UA');
        // setOutLevel(50)
        // setPatch(1);
        // setBank2('UA');
        // setPatch2(1);
        // setOutLevel2(50);
    };

    const handleUpdate = () => {
        if (selectedRow !== null) {
            const newData = [...data];
            const index = newData.findIndex((r) => r.id === selectedRow.id);
            newData[index] = {id: selectedRow.id, title, bank, patch, outLevel, bank2, patch2, outLevel2};
            setData(newData);
            // setSelectedRow(null);
        }
    };

    const handleRemove = () => {
        if (selectedRow !== null) {
            setData(data.filter((row) => row.id !== selectedRow.id));
            setSelectedRow(null);
        }
    };

    const handleRowClick = (row) => {
        row = modifyRow(row);
        setSelectedRow(row);
        setTitle(row.title);
        setBank(row.bank);
        setPatch(row.patch);
        setOutLevel(row.outLevel ? row.outLevel : 50);
        setBank2(row.bank2);
        setPatch2(row.patch2);
        setOutLevel2(row.outLevel2 ? row.outLevel2 : 50);
    };

    const clearAll = () => {
        setData([]);
        setSelectedRow(null);
    }

    const swapWithAbove = () => {
        const newData = [...data];
        const index = newData.findIndex((row) => row.id === selectedRow.id);
        if (index > 0) {
            [newData[index - 1], newData[index]] = [newData[index], newData[index - 1]];
            setSelectedRow(newData[index - 1]);
        }
        setData(newData);
    }

    const swapWithBelow = () => {
        const newData = [...data];
        const index = newData.findIndex((row) => row.id === selectedRow.id);
        if (index < newData.length - 1) {
            [newData[index + 1], newData[index]] = [newData[index], newData[index + 1]];
            setSelectedRow(newData[index + 1]);
        }
        setData(newData);
    }

    const handleContextMenu = (event) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : null,
        );
    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const handleExport = () => {
        const blob = new Blob([tsv], { type: "text/plain;charset=utf-8" });
        saveAs(blob, "VF1CON.txt");
        setContextMenu(null);
    };

    const handleFileDroped = (contents) => {
        setData(fromTsv(contents));
    };

    // const handleSendSrv33 = () => {
    //     if (midiOut) {
    //         const channel = midiChannelSrv33 - 1;
    //         // const deviceId = deviceIdSrv33;
    //         const bankNum = 1;
    //         const patchArg = 4;
    //         const bankSelectMSB = [0xB0 + channel, 0x00, bankNum]; // Bank Select MSB
    //         const programChangeMessage = [0xC0 + channel, patchArg - 1];　// bank
    //
    //
    //         midiOutSrv33.send(bankSelectMSB);
    //         midiOutSrv33.send(programChangeMessage);
    //
    //         // const revLevel = 100;
    //         // const revTime = 82;
    //         //
    //         // // const addressRevTimeA = [0x00, 0x00, 0x00, 0x39];
    //         // // const addressRevLevelA = [0x00, 0x00, 0x00, 0x3B];
    //         // const addressRevTimeB = [0x00, 0x00, 0x01, 0x0D];
    //         // const addressRevLevelB = [0x00, 0x00, 0x01, 0x0F];
    //         //
    //         // const sendExclusiveMessage = (address, value) => {
    //         //     const content = [0x00, 0x00, Math.floor(value / 16) , value % 16];
    //         //     const dataSetMessage = [0xF0, 0x41, deviceId, 0x00, 0x12, 0x12, ...address, ...content, calcChecksum([...address, ...content]), 0xF7];
    //         //     midiOut.send(dataSetMessage);
    //         // }
    //         //
    //         // sendExclusiveMessage(addressRevTimeB, revTime);
    //         // sendExclusiveMessage(addressRevLevelB, revLevel);
    //     }
    // };

    return (
        <>
            {!isSmallScreen && (
            <StyledBackdrop open={midiAccessStatus !== MidiAccessStatus.success}>
                {midiAccessStatus === MidiAccessStatus.initializing && (
                    <CircularProgress color="inherit" />
                )}
                <Typography variant="h6" sx={{ marginLeft: '20px' }}>
                    {midiAccessStatus === MidiAccessStatus.initializing ? 'MIDIデバイスへのアクセスを要求中...' :
                        midiAccessStatus === MidiAccessStatus.denied ? 'MIDIデバイスへのアクセスが拒否されました。' :
                            midiAccessStatus === MidiAccessStatus.unsupported ? 'このブラウザはWebMIDIをサポートしていません。' : ''}
                </Typography>
            </StyledBackdrop>)}
            <DroppablBody onFileContents={handleFileDroped}>
                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="simple tabs example" style={{ display: 'none' }}>
                    <Tab label="List" />
                    <Tab label="TSV" />
                </Tabs>
                <TabPanel value={tabIndex} index={0}>
                </TabPanel>
                <TabPanel value={tabIndex} index={1}>
                    <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={tsv}
                        onChange={handleTsvChange}
                    />
                </TabPanel>
                <StyledTableContainer component={Paper} style={isSmallScreen ? { top: 0, height: "100vh" } : {}}>
                    <div onContextMenu={handleContextMenu} style={{ cursor: 'context-menu' }}>
                        {!isSmallScreen && (
                        <HeaderStyledTTable size="small" sx={{
                            [`& .${tableCellClasses.root}`]: {
                                borderBottom: "none"
                            }
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {/*<Box display='flex'>*/}
                                        {/*    <Box display='flex' alignItems='center' padding='10px'>*/}
                                        {/*        SRV3030 Control*/}
                                        {/*    </Box>*/}
                                        {/*    <Box width='300px'>*/}
                                        {/*        <MidiOutSelector setMidiOut={setMidiOutSrv33} setSMidiAccessStatus={setMidiAccessStatus} initialId={midiOutIdSrv33} setInitialId={setMidiOutIdSrv33}/>*/}
                                        {/*    </Box>*/}
                                        {/*    <Box width='100px'>*/}
                                        {/*        <ChannelSelector channel={midiChannelSrv33} setChannel={setMidiChannelSrv33}/>*/}
                                        {/*    </Box>*/}
                                        {/*    /!*<Box width='100px'>*!/*/}
                                        {/*    /!*    <DeviceIdSelector deviceId={deviceIdSrv33} setCDeviceId={setDeviceIdSrv33}/>*!/*/}
                                        {/*    /!*</Box>*!/*/}
                                        {/*    <Box display='flex' alignItems='center' padding='10px'>*/}
                                        {/*        <Button onClick={handleSendSrv33} variant="contained" sx={{ textTransform: 'none' }}>*/}
                                        {/*            Send*/}
                                        {/*        </Button>*/}
                                        {/*    </Box>*/}
                                        {/*</Box>*/}
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <MidiOutSelector setMidiOut={setMidiOut} initialId={midiOutId} setInitialId={setMidiOutId} midiOutputs={midiOutputs}/>
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <MidiOutSelector setMidiOut={setMidiOut2} initialId={midiOutId2} setInitialId={setMidiOutId2} midiOutputs={midiOutputs}/>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell rowSpan={2} style={{ verticalAlign: 'bottom' }} >
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 2 }}>
                                            <Button onClick={clearAll} variant="contained" color="error" sx={{ textTransform: 'none' }} style={{ width: '60px', height: '25px', marginRight: '10px' }}>
                                                Clear
                                            </Button>
                                            <Button onClick={swapWithAbove} variant="contained" color="primary" style={{ width: '60px', height: '25px', marginRight: '10px' }} disabled={selectedRow === null}>
                                                ↑
                                            </Button>
                                            <Button onClick={swapWithBelow} variant="contained" color="primary" style={{ width: '60px', height: '25px', marginRight: '10px' }} disabled={selectedRow === null}>
                                                ↓
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell colSpan={2}>
                                        <ChannelSelector channel={midiChannel} setChannel={setMidiChannel}/>
                                    </TableCell>
                                    <TableCell>
                                        <DeviceIdSelector deviceId={deviceId} setCDeviceId={setDeviceId}/>
                                    </TableCell>
                                    <TableCell colSpan={2}>
                                        <ChannelSelector channel={midiChannel2} setChannel={setMidiChannel2}/>
                                    </TableCell>
                                    <TableCell>
                                        <DeviceIdSelector deviceId={deviceId2} setCDeviceId={setDeviceId2}/>
                                    </TableCell>
                                </TableRow>
                                <TableRow style={{ borderBottom: 'none' }}>
                                    <TableCell colSpan={3}>
                                        <OutLevelSelector value={outLevel * 2} setValue={(value) => setOutLevel(value / 2)}/>
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <OutLevelSelector value={outLevel2 * 2} setValue={(value) => setOutLevel2(value / 2)}/>
                                    </TableCell>
                                </TableRow>
                                <TableRow style={{ backgroundColor: '#3f51b5', color: '#fff' }}>
                                    <TableCell style={{ color: '#fff', fontWeight: 'bold' }}>Title</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Bank</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Patch</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Out Level</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Bank</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Patch</TableCell>
                                    <TableCell align={"center"} style={{ color: '#fff', fontWeight: 'bold', width: '100px', maxWidth: '100px', overflow: 'hidden', }}>Out Level</TableCell>
                                </TableRow>
                            </TableHead>
                        </HeaderStyledTTable>)}
                        <Table size="small">
                            <TableBody>
                                {data.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        onClick={() => handleRowClick(row)}
                                        style={{ backgroundColor: selectedRow && selectedRow.id === row.id ? '#d3d3d3' : '#fff' }}
                                        ref={selectedRowRef}
                                    >
                                        {isSmallScreen ?
                                            <TableCell style={{ width: '200px', minWidth: '200px' }}>{row.title}</TableCell> :
                                            <TableCell>{row.title}</TableCell>
                                        }
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.bank}</TableCell>
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.patch}</TableCell>
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.outLevel ? `${row.outLevel * 2}%` : ''}</TableCell>
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.bank2}</TableCell>
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.patch2}</TableCell>
                                        <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>{row.outLevel2 ? `${row.outLevel2 * 2}%` : ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Menu
                            open={contextMenu !== null}
                            onClose={handleClose}
                            anchorReference="anchorPosition"
                            anchorPosition={
                                contextMenu !== null
                                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                                    : undefined
                            }
                        >
                            <MenuItem onClick={handleExport}>Export</MenuItem>
                        </Menu>
                    </div>
                    {!isSmallScreen && (
                    <FooterStyledTTable>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                            <Button
                                                variant="contained" color="primary" onClick={handleAdd} disabled={title === ''}
                                                style={{ width: '50px', height: '25px', marginRight: '10px' }}>ADD</Button>
                                            <Button
                                                variant="contained" color="primary" onClick={handleUpdate} disabled={selectedRow === null || title === ''}
                                                style={{ width: '50px', height: '25px', marginRight: '10px' }}>UPD</Button>
                                            <Button
                                                variant="contained" color="secondary" onClick={handleRemove} disabled={selectedRow === null}
                                                style={{ width: '50px', height: '25px', marginRight: '10px', marginBottom: '20px' }}>DEL</Button>
                                        </Box>
                                        <TextField value={title} onChange={(e) => setTitle(e.target.value)} label="Title" variant="outlined" style={{ width: '300px' }}/>
                                    </Box>
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>
                                    <FormControl
                                        variant="outlined"
                                        // sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}
                                        style={{ width: '100%'}}
                                    >
                                        <InputLabel id="bank-label">Bank</InputLabel>
                                        <Select
                                            labelId="bank-label"
                                            value={bank}
                                            onChange={(e) => setBank(e.target.value)}
                                            label="Bank"
                                            style={{ width: '100%'}}
                                        >
                                            <MenuItem value={'PA'}>PA</MenuItem>
                                            <MenuItem value={'PB'}>PB</MenuItem>
                                            <MenuItem value={'UA'}>UA</MenuItem>
                                            <MenuItem value={'UB'}>UB</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden' }}>
                                    <TextField
                                        value={patch}
                                        onChange={(e) => setPatch(e.target.value)}
                                        type="number"
                                        label="Patch"
                                        // variant="outlined"
                                        // sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                        InputProps={{ inputProps: { min: 1, max: 101 } }}
                                        style={{ width: '100%', margin: '0px', padding: '0px' }}
                                    />
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>
                                    <OutLevelSelectorClickAway value={outLevel * 2} setValue={(value) => setOutLevel(value / 2)}/>
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>
                                    <FormControl
                                        variant="outlined"
                                        // sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}
                                        style={{ width: '100%'}}
                                    >
                                        <InputLabel id="bank-label">Bank</InputLabel>
                                        <Select
                                            labelId="bank-label"
                                            value={bank2}
                                            onChange={(e) => setBank2(e.target.value)}
                                            label="Bank"
                                            style={{ width: '100%'}}
                                        >
                                            <MenuItem value={'PA'}>PA</MenuItem>
                                            <MenuItem value={'PB'}>PB</MenuItem>
                                            <MenuItem value={'UA'}>UA</MenuItem>
                                            <MenuItem value={'UB'}>UB</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden' }}>
                                    <TextField
                                        value={patch2}
                                        onChange={(e) => setPatch2(e.target.value)}
                                        type="number"
                                        label="Patch"
                                        // variant="outlined"
                                        // sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                        InputProps={{ inputProps: { min: 1, max: 101 } }}
                                        style={{ width: '100%', margin: '0px', padding: '0px' }}
                                    />
                                </TableCell>
                                <TableCell align={"center"} style={{ width: '100px', maxWidth: '100px', overflow: 'hidden', }}>
                                    <OutLevelSelectorClickAway value={outLevel2 * 2} setValue={(value) => setOutLevel2(value / 2)}/>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </FooterStyledTTable>)}
                </StyledTableContainer>
            </DroppablBody>
        </>
    );
}
